// Choisir dans une longue liste en tapant, plutôt qu'en déroulant.
//
// La liste déroulante du navigateur va bien à dix entrées. À deux cents — les
// clients, les chantiers — on fait défiler à l'aveugle sur un téléphone, au
// soleil, avec des gants.
//
// Le champ reste UN CHAMP DE TEXTE ORDINAIRE : flèches pour parcourir, Entrée
// pour choisir, Échap pour sortir sans rien changer, et la liste entière
// s'ouvre au clic — rien n'est perdu pour qui ne veut pas taper.
//
// Ce composant ne sait rien de ce qu'il affiche : il reçoit une fonction de
// recherche et le libellé de l'élément choisi. Les spécialisations (client,
// chantier) vivent à côté de leurs données.

import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/** Une proposition telle qu'elle s'affiche : un nom, et une ligne de détail. */
export interface OptionChoix {
  id: string;
  nom: string;
  detail: string;
}

/** Au-delà, on arrête d'afficher et on invite à préciser : la liste ne sert plus. */
const MAX_AFFICHES = 40;

interface ChoixListeProps {
  /** Identifiant du champ, pour le <Label htmlFor>. */
  id?: string;
  /** Ce qui correspond à cette recherche ; une saisie vide rend TOUT. */
  filtrer: (saisie: string) => OptionChoix[];
  /** L'élément actuellement choisi, pour l'afficher dans le champ. */
  choisi: OptionChoix | null;
  /** Identifiant choisi, chaîne vide si aucun. */
  value: string;
  onChange: (id: string) => void;
  /** « client », « chantier » : sert aux libellés d'accessibilité et aux messages. */
  nomObjet: string;
  nomPluriel: string;
  placeholder?: string;
  /** Libellé de l'option « rien ». Absent : le choix est obligatoire. */
  labelAucun?: string;
  disabled?: boolean;
  /** Les données ne sont pas encore arrivées. */
  chargement?: boolean;
  /** Rien n'est enregistré du tout — à distinguer d'une recherche infructueuse. */
  aucunEnregistre?: boolean;
  /** Le champ est en erreur (choix obligatoire non renseigné). */
  invalide?: boolean;
}

export function ChoixListe({
  id,
  filtrer,
  choisi,
  value,
  onChange,
  nomObjet,
  nomPluriel,
  placeholder = 'Taper pour chercher…',
  labelAucun,
  disabled = false,
  chargement = false,
  aucunEnregistre = false,
  invalide = false,
}: ChoixListeProps) {
  const idListe = useId();
  const boite = useRef<HTMLDivElement>(null);
  const champ = useRef<HTMLInputElement>(null);
  const lignes = useRef<(HTMLButtonElement | null)[]>([]);
  /**
   * Choisir à la souris fait perdre le focus au champ ; on le lui rend pour que
   * la tabulation reparte d'ici. Ce retour de focus ne doit PAS rouvrir la
   * liste qu'on vient tout juste de fermer.
   */
  const focusSilencieux = useRef(false);

  const [ouvert, setOuvert] = useState(false);
  /** L'utilisateur a tapé quelque chose depuis l'ouverture. */
  const [tape, setTape] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [actif, setActif] = useState(0);

  // Tant qu'on ne tape pas, le champ montre ce qui est choisi : on voit ce qui
  // est enregistré, et la liste propose tout.
  useEffect(() => {
    if (!tape) setSaisie(choisi?.nom ?? '');
  }, [choisi, tape]);

  const trouves = useMemo(() => filtrer(tape ? saisie : ''), [filtrer, saisie, tape]);

  const options = useMemo((): OptionChoix[] => {
    const o = trouves.slice(0, MAX_AFFICHES);
    if (labelAucun !== undefined) o.unshift({ id: '', nom: labelAucun, detail: '' });
    return o;
  }, [trouves, labelAucun]);

  const reste = Math.max(0, trouves.length - MAX_AFFICHES);

  // La ligne mise en avant reste dans la fenêtre pendant qu'on descend.
  useEffect(() => {
    if (ouvert) lignes.current[actif]?.scrollIntoView({ block: 'nearest' });
  }, [actif, ouvert]);

  // Un clic ailleurs referme et remet le libellé de l'élément choisi.
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e: PointerEvent): void => {
      if (!boite.current?.contains(e.target as Node)) fermer();
    };
    document.addEventListener('pointerdown', dehors);
    return () => {
      document.removeEventListener('pointerdown', dehors);
    };
  });

  function ouvrir(): void {
    if (disabled) return;
    setOuvert(true);
    setTape(false);
    // On se place sur l'élément déjà choisi, pas en haut de la liste.
    const i = options.findIndex((o) => o.id === value);
    setActif(i < 0 ? 0 : i);
  }

  function fermer(): void {
    setOuvert(false);
    setTape(false);
    setSaisie(choisi?.nom ?? '');
  }

  function choisir(idChoisi: string): void {
    onChange(idChoisi);
    setOuvert(false);
    setTape(false);
    setSaisie(options.find((o) => o.id === idChoisi && o.id !== '')?.nom ?? '');
    focusSilencieux.current = true;
    champ.current?.focus();
  }

  function vider(): void {
    onChange('');
    setSaisie('');
    setTape(false);
    champ.current?.focus();
  }

  function touche(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!ouvert) {
        ouvrir();
        return;
      }
      const pas = e.key === 'ArrowDown' ? 1 : -1;
      setActif((i) => {
        if (options.length === 0) return 0;
        return (i + pas + options.length) % options.length;
      });
      return;
    }
    if (e.key === 'Enter') {
      // Sans cela, Entrée enverrait le formulaire au lieu de choisir.
      if (ouvert) {
        e.preventDefault();
        const o = options[actif];
        if (o) choisir(o.id);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (ouvert) {
        e.preventDefault();
        fermer();
      }
      return;
    }
    if (e.key === 'Tab' && ouvert) fermer();
  }

  const messageVide = chargement
    ? `Chargement des ${nomPluriel}…`
    : aucunEnregistre
      ? `Aucun ${nomObjet} enregistré.`
      : `Aucun ${nomObjet} ne correspond.`;

  return (
    <div ref={boite} className="relative">
      <div className="relative">
        <Search
          className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
          aria-hidden
        />
        <input
          ref={champ}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={ouvert}
          aria-controls={idListe}
          aria-autocomplete="list"
          aria-invalid={invalide || undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={saisie}
          className={cn(
            'border-input bg-card ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-12 w-full rounded-md border pl-9 pr-16 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 lg:h-11',
            invalide && 'border-destructive',
          )}
          onChange={(e) => {
            setSaisie(e.target.value);
            setTape(true);
            setOuvert(true);
            setActif(0);
          }}
          onFocus={(e) => {
            if (focusSilencieux.current) {
              focusSilencieux.current = false;
              return;
            }
            ouvrir();
            // Le premier caractère tapé remplace le libellé affiché.
            e.target.select();
          }}
          onClick={() => {
            if (!ouvert) ouvrir();
          }}
          onKeyDown={touche}
        />
        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
          {value !== '' && !disabled ? (
            <button
              type="button"
              aria-label={`Retirer le ${nomObjet}`}
              className="text-muted-foreground hover:text-foreground flex h-10 w-8 items-center justify-center rounded"
              onClick={vider}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
          <button
            type="button"
            tabIndex={-1}
            aria-label={ouvert ? 'Fermer la liste' : `Voir tous les ${nomPluriel}`}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground flex h-10 w-8 items-center justify-center rounded"
            onClick={() => {
              if (ouvert) fermer();
              else {
                ouvrir();
                champ.current?.focus();
              }
            }}
          >
            <ChevronDown className={cn('h-4 w-4 transition-transform', ouvert && 'rotate-180')} />
          </button>
        </div>
      </div>

      {ouvert ? (
        <ul
          id={idListe}
          role="listbox"
          className="border-input bg-card absolute z-50 mt-1 max-h-72 w-full overflow-y-auto overscroll-contain rounded-md border py-1 shadow-lg"
        >
          {options.length === 0 ? (
            <li className="text-muted-foreground px-3 py-3 text-sm">{messageVide}</li>
          ) : (
            options.map((o, i) => (
              <li key={o.id === '' ? '__aucun' : o.id}>
                <button
                  ref={(el) => {
                    lignes.current[i] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={o.id === value}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left',
                    i === actif ? 'bg-muted' : 'hover:bg-muted/60',
                  )}
                  onPointerEnter={() => {
                    setActif(i);
                  }}
                  onClick={() => {
                    choisir(o.id);
                  }}
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate',
                        o.id === '' ? 'text-muted-foreground' : 'font-medium',
                      )}
                    >
                      {o.nom}
                    </span>
                    {o.detail === '' ? null : (
                      <span className="text-muted-foreground block truncate text-xs">
                        {o.detail}
                      </span>
                    )}
                  </span>
                  {o.id === value ? (
                    <Check className="text-primary h-4 w-4 shrink-0" aria-hidden />
                  ) : null}
                </button>
              </li>
            ))
          )}
          {reste > 0 ? (
            <li className="text-muted-foreground border-t px-3 py-2 text-xs">
              et {reste} autre{reste > 1 ? 's' : ''} — précisez la recherche
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
